import { AlertTriangle, LockKeyhole } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useRenovacaoSnapshot } from '@/hooks/useRenovacaoParametros';
import { useTabelaRenovacaoPedido, finalDaLinha } from '@/hooks/comissao/useTabelaRenovacaoPedido';

interface Props {
  pedidoId: string;
  leadId: string;
}

interface SnapshotLinha {
  id: string;
  msisdn: string;
  plano_atual: string | null;
  gb_atual: number;
  gb_minimo: number;
  ticket_atual: number;
  plano_novo: string | null;
  gb_novo: number | null;
  ticket_novo: number;
  receita_renovada: number;
  receita_adicionada: number;
  delta_economico: number | null;
  delta_literal: number | null;
}

interface SnapshotPayload {
  versao: string;
  criadoEm: string;
  deltaFormulaSign: 'ECONOMICO' | 'LITERAL_REGULAMENTO';
  convergenciaStatus?: 'NAO_APLICAVEL' | 'SEM_VINCULO' | 'VINCULO_INVALIDO' | 'PENDENTE_INSTALACAO' | 'VALIDADA' | 'FORA_DA_COMPETENCIA';
  enquadramento: { tabela_apurada: string; modo_agrupamento: string; mes_competencia: string };
  linhas: SnapshotLinha[];
}

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const GRID = 'grid grid-cols-[1.05fr_1.5fr_.75fr_1.6fr_.75fr_.75fr_.8fr_1fr_.5fr] gap-2 px-2';
const GRID_VALOR = 'grid grid-cols-[1.05fr_1.5fr_.75fr_1.6fr_.75fr_.75fr_.8fr_1fr_.5fr_.6fr_.8fr] gap-2 px-2';
const ROTULO_TABELA = { '1': 'Tabela 1 · convergente', '2': 'Tabela 2', PENDENTE: 'Tabela pendente' } as const;

export function RenovacaoSnapshotPedido({ pedidoId, leadId }: Props) {
  const { data: snapshot, isLoading } = useRenovacaoSnapshot(pedidoId, leadId);
  const data = snapshot as SnapshotPayload | undefined;
  const { data: tabelaPedido } = useTabelaRenovacaoPedido(pedidoId);
  const valores = tabelaPedido?.valores ?? null;
  const linhasValor = (valores?.itens || []).flatMap((i) => i.linhas || []);
  const faixaDa = (msisdn: string) => tabelaPedido?.linhas.find((l) => finalDaLinha(l.linha) === finalDaLinha(msisdn))?.faixa ?? null;
  const valorDa = (msisdn: string) => linhasValor.find((l) => finalDaLinha(l.linha) === finalDaLinha(msisdn));
  const grade = valores ? GRID_VALOR : GRID;
  const largura = valores ? 'min-w-[1240px]' : 'min-w-[1100px]';

  if (isLoading) return <div className="h-20 animate-pulse rounded-md bg-muted" />;
  if (!data) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Renovação legada: este pedido foi criado antes do cálculo por linha e permanece somente para consulta. O CRM não reconstrói Delta Ticket sem o histórico confiável da conta.</AlertDescription>
      </Alert>
    );
  }

  const linhas = data.linhas || [];
  const totalAtual = linhas.reduce((soma, linha) => soma + Number(linha.ticket_atual || 0), 0);
  const totalRenovada = linhas.reduce((soma, linha) => soma + Number(linha.receita_renovada || 0), 0);
  const totalAdicionada = linhas.reduce((soma, linha) => soma + Number(linha.receita_adicionada || 0), 0);

  return (
    <Card className="bg-card border border-border border-l-[3px] border-l-primary rounded-r-lg rounded-l-none">
      <CardHeader className="pb-3">
        <div className="flex items-center flex-nowrap justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-sm"><LockKeyhole className="h-4 w-4 text-primary" />Comparativo fechado da renovação</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">Registro imutável · {data.versao} · {data.enquadramento.mes_competencia}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {tabelaPedido && (
              <Badge className={cn('font-bold', tabelaPedido.tabela === 'PENDENTE' ? 'text-primary border-primary' : '')} variant="outline">
                {ROTULO_TABELA[tabelaPedido.tabela]}{valores ? ` · cluster ${valores.cluster}` : ''}
              </Badge>
            )}
            <Badge className="font-bold" variant="outline">{linhas.length} linhas</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.convergenciaStatus === 'PENDENTE_INSTALACAO' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>Convergência pendente no fechamento: a Fixa vinculada ainda aguardava instalação e pode reenquadrar o CNPJ.</AlertDescription>
          </Alert>
        )}
        <div className="flex min-h-[240px] flex-col overflow-hidden rounded-md border">
          <div className="min-h-0 flex-1 overflow-auto">
            <div className={cn(grade, largura, 'sticky top-0 z-10 h-8 items-center bg-muted text-xs font-bold uppercase tracking-wider text-muted-foreground')}>
              <span>Linha</span><span>Plano atual</span><span>Valor atual</span><span>Plano novo</span><span>Valor novo</span><span>Adicional</span><span>Total novo</span><span>Delta</span><span>Faixa</span>
              {valores && <><span>%</span><span>Comissão</span></>}
            </div>
            {linhas.map((linha) => {
              const delta = data.deltaFormulaSign === 'LITERAL_REGULAMENTO' ? linha.delta_literal : linha.delta_economico;
              return (
                <div key={linha.id} className={cn(grade, largura, 'h-8 items-center overflow-hidden border-t text-sm tabular-nums')}>
                  <span className="truncate font-mono" title={linha.msisdn}>{linha.msisdn}</span>
                  <span className="truncate" title={`${linha.plano_atual || 'Não informado'} · ${linha.gb_atual}GB`}>{linha.plano_atual || 'Não informado'} · {linha.gb_atual}GB</span>
                  <span className="truncate text-primary">{moeda.format(linha.ticket_atual)}</span>
                  <span className="truncate" title={`${linha.plano_novo || 'Não informado'} · ${linha.gb_novo ?? '—'}GB`}>{linha.plano_novo || 'Não informado'} · {linha.gb_novo ?? '—'}GB</span>
                  <span className="truncate text-primary">{moeda.format(linha.receita_renovada)}</span>
                  <span className="truncate text-primary">{moeda.format(linha.receita_adicionada)}</span>
                  <span className="truncate text-primary">{moeda.format(linha.ticket_novo)}</span>
                  <span className="truncate text-primary">{delta === null ? 'Inválido' : `${delta > 0 ? '+' : ''}${Number(delta).toFixed(2).replace('.', ',')}%`}</span>
                  <span className="truncate font-bold">{faixaDa(linha.msisdn) ? `F${faixaDa(linha.msisdn)}` : '—'}</span>
                  {valores && (() => {
                    const v = valorDa(linha.msisdn);
                    return <><span className="truncate">{v ? `${String(v.pct).replace('.', ',')}%` : '—'}</span><span className="truncate font-bold text-primary">{v ? moeda.format(v.comissao) : '—'}</span></>;
                  })()}
                </div>
              );
            })}
          </div>
          <div className={cn(grade, largura, 'flex-none h-8 items-center bg-muted border-t-2 border-border text-sm font-bold tabular-nums')}>
            <span>TOTAL · {linhas.length}</span><span /><span>{moeda.format(totalAtual)}</span><span /><span>{moeda.format(totalRenovada)}</span><span>{moeda.format(totalAdicionada)}</span><span>{moeda.format(totalRenovada + totalAdicionada)}</span><span className="truncate text-muted-foreground" title="O Delta é apurado somente por linha">Sem Delta agregado</span><span />
            {valores && <><span /><span className="text-primary">{moeda.format(linhasValor.reduce((s, l) => s + l.comissao, 0))}</span></>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
